// src/components/roads/ponding/RoadTerrainPondingTab.js
// Works Report tab "Terrain & ponding" for ONE road:
//   • ground profile along the road (TessaDEM, segment start / mid / end) with the forecast peak water level
//   • per segment: ground, grade, low point, forecast standing water, when it starts, how long it stays, linked drain
//   • "Refresh elevation" (slices the road if needed, re-fetches every segment) and "Recompute forecast"
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, SimpleGrid, Skeleton, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack,
    useColorModeValue, useToast,
} from '@chakra-ui/react';
import { MdTerrain, MdRefresh, MdWaterDrop } from 'react-icons/md';
import { getRoadPonding, refreshRoadElevation } from './pondingApi';
import { bandMeta, fmtDepth, fmtWater, fmtHours, fmtClears, KIND_LABEL } from './pondingUi';

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const fmtM = (v) => (v == null ? '—' : `${Number(v).toFixed(2)} m`);

/** Ground line + water at its peak level along the road (single y axis: metres above sea level). */
const ProfileChart = memo(({ profile }) => {
    const ground = useColorModeValue('#975A16', '#D69E2E');
    const water = '#3182CE';
    const grid = useColorModeValue('#E2E8F0', 'rgba(255,255,255,0.08)');
    const muted = useColorModeValue('#4A5568', '#A0AEC0');
    const tipBg = useColorModeValue('white', 'gray.700');
    const ref = useRef(null);
    const [hover, setHover] = useState(null);
    const pts = useMemo(() => (profile || []).filter((p) => p.e != null && p.ch != null), [profile]);
    if (pts.length < 2) return <Text fontSize="13px" color={muted}>Not enough ground points to draw the profile.</Text>;
    const W = 700; const H = 170; const P = { l: 44, r: 8, t: 10, b: 22 };
    const x0 = pts[0].ch; const x1 = pts[pts.length - 1].ch;
    const lo = Math.min(...pts.map((p) => p.e)); const hi = Math.max(...pts.map((p) => p.e + (p.peak_depth_m || 0)));
    const pad = Math.max(0.2, (hi - lo) * 0.1);
    const y0 = lo - pad; const y1 = hi + pad;
    const sx = (x) => P.l + ((x - x0) / Math.max(1, x1 - x0)) * (W - P.l - P.r);
    const sy = (y) => P.t + (1 - (y - y0) / (y1 - y0)) * (H - P.t - P.b);
    const groundPath = pts.map((p, i) => `${i ? 'L' : 'M'}${sx(p.ch).toFixed(1)},${sy(p.e).toFixed(1)}`).join('');
    const wetRuns = []; let cur = [];
    pts.forEach((p) => { if ((p.peak_depth_m || 0) > 0.005) cur.push(p); else { if (cur.length) wetRuns.push(cur); cur = []; } });
    if (cur.length) wetRuns.push(cur);
    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect(); if (!r) return;
        const x = ((e.clientX - r.left) / r.width) * W;
        let best = 0; let bd = Infinity; pts.forEach((p, i) => { const d = Math.abs(sx(p.ch) - x); if (d < bd) { bd = d; best = i; } });
        setHover(best);
    };
    const hp = hover != null ? pts[hover] : null;
    const ticks = [y0 + pad, (y0 + y1) / 2, y1 - pad];
    return (
        <Box position="relative">
            <svg ref={ref} viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Ground profile along the road with the forecast water level"
                onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ display: 'block' }}>
                {ticks.map((t) => (
                    <g key={t}><line x1={P.l} x2={W - P.r} y1={sy(t)} y2={sy(t)} stroke={grid} vectorEffect="non-scaling-stroke" />
                        <text x={P.l - 4} y={sy(t) + 3} textAnchor="end" fontSize="10" fill={muted}>{t.toFixed(1)}</text></g>
                ))}
                {wetRuns.map((run, i) => (
                    <path key={i} fill={water} opacity="0.45"
                        d={`${run.map((p, k) => `${k ? 'L' : 'M'}${sx(p.ch).toFixed(1)},${sy(p.e + p.peak_depth_m).toFixed(1)}`).join('')}${[...run].reverse().map((p) => `L${sx(p.ch).toFixed(1)},${sy(p.e).toFixed(1)}`).join('')}Z`} />
                ))}
                <path d={groundPath} fill="none" stroke={ground} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
                {hp && <line x1={sx(hp.ch)} x2={sx(hp.ch)} y1={P.t} y2={H - P.b} stroke={muted} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
            </svg>
            <Flex justify="space-between" fontSize="10px" color={muted} pl={`${(P.l / W) * 100}%`}>
                <Text>{Math.round(x0)} m</Text>
                <HStack spacing={3}><HStack spacing={1}><Box w="10px" h="2px" bg={ground} /><Text>Ground (TessaDEM, m above sea level)</Text></HStack><HStack spacing={1}><Box w="10px" h="10px" bg={water} opacity={0.45} /><Text>Forecast peak water</Text></HStack></HStack>
                <Text>{Math.round(x1)} m</Text>
            </Flex>
            {hp && (
                <Box position="absolute" top={0} right={0} bg={tipBg} borderWidth="1px" borderRadius="md" px={2} py={1} boxShadow="sm" pointerEvents="none">
                    <Text fontSize="11px" fontWeight="800">Chainage {Math.round(hp.ch)} m</Text>
                    <Text fontSize="11px">Ground {hp.e.toFixed(2)} m{hp.peak_depth_m > 0.005 ? ` · water ${fmtDepth(hp.peak_depth_m)}` : ''}</Text>
                </Box>
            )}
        </Box>
    );
});
ProfileChart.displayName = 'ProfileChart';
ProfileChart.propTypes = { profile: PropTypes.array };

function RoadTerrainPondingTab({ roadId, roadName, segments = [], onChanged }) {
    const toast = useToast();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const seq = useRef(0);
    const headBg = useColorModeValue('gray.50', 'gray.900');
    const muted = useColorModeValue('gray.600', 'gray.400');
    const card = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');

    const load = useCallback(async (force = false) => {
        if (!roadId) return;
        const my = ++seq.current; setLoading(true);
        try {
            const res = await getRoadPonding(roadId, { force });
            if (my === seq.current) { setData(res?.data || null); setError(null); }
        } catch (e) { if (my === seq.current) setError(e?.message || 'Ponding forecast unavailable.'); }
        finally { if (my === seq.current) setLoading(false); }
    }, [roadId]);
    useEffect(() => { setData(null); load(false); }, [load]);

    const refreshElevation = useCallback(async () => {
        setRefreshing(true);
        try {
            const res = await refreshRoadElevation(roadId);
            const d = res?.data || {};
            toast({ status: 'success', title: 'Road elevation updated', description: `${d.segments_filled ?? 0} segment(s) from TessaDEM${d.sliced ? ' · road sliced into segments' : ''}.`, duration: 4000, position: 'top-right' });
            onChanged?.();
            await load(true);
        } catch (e) {
            toast({ status: 'warning', title: 'Elevation not updated', description: e?.message, duration: 6000, position: 'top-right' });
        } finally { setRefreshing(false); }
    }, [roadId, load, onChanged, toast]);

    const byId = useMemo(() => Object.fromEntries((data?.segments || []).map((s) => [String(s.segment_id), s])), [data]);
    const rows = useMemo(() => [...segments].sort((a, b) => num(a.segment_index) - num(b.segment_index)), [segments]);
    const withElev = rows.filter((s) => num(s.ground_start_elev_m) != null || num(s.ground_end_elev_m) != null).length;
    const lows = rows.map((s) => num(s.ground_low_elev_m) ?? Math.min(...[s.ground_start_elev_m, s.ground_mid_elev_m, s.ground_end_elev_m].map(num).filter((v) => v != null))).filter(Number.isFinite);
    const ponding = (data?.segments || []).filter((s) => s.band !== 'none');
    const road = data?.road;

    return (
        <VStack align="stretch" spacing={4}>
            <Flex justify="space-between" align="center" gap={2} wrap="wrap">
                <HStack spacing={2}>
                    <Icon as={MdTerrain} color="orange.400" boxSize={5} />
                    <Box>
                        <Text fontSize="sm" fontWeight="800">Terrain &amp; ponding — {roadName || data?.road?.road_name || roadId}</Text>
                        <Text fontSize="12px" color={muted}>Ground from TessaDEM at every segment&apos;s start, middle and end; water from the live rainfall × drainage forecast.</Text>
                    </Box>
                </HStack>
                <HStack spacing={2}>
                    <Button size="sm" variant="outline" leftIcon={<Icon as={MdTerrain} />} onClick={refreshElevation} isLoading={refreshing} loadingText="Fetching…">Refresh elevation</Button>
                    <Button size="sm" colorScheme="blue" leftIcon={<Icon as={MdRefresh} />} onClick={() => load(true)} isLoading={loading && !!data}>Recompute forecast</Button>
                </HStack>
            </Flex>

            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3}>
                {[
                    ['Segments with ground', `${withElev} / ${rows.length}`, withElev < rows.length ? 'orange.500' : undefined],
                    ['Lowest ground', lows.length ? `${Math.min(...lows).toFixed(2)} m` : '—'],
                    ['Highest ground', lows.length ? `${Math.max(...rows.map((s) => Math.max(...[s.ground_start_elev_m, s.ground_mid_elev_m, s.ground_end_elev_m].map(num).filter((v) => v != null), -Infinity)).filter(Number.isFinite)).toFixed(2)} m` : '—'],
                    ['Segments with water', ponding.length, ponding.length ? bandMeta(road?.band).hex : undefined],
                    ['Deepest / starts', road ? `${fmtDepth(road.max_depth_m)} · ${fmtHours(road.first_hours)}` : '—'],
                ].map(([l, v, c]) => (
                    <Box key={l} bg={card} borderWidth="1px" borderColor={border} borderRadius="lg" p={2.5}>
                        <Text fontSize="11px" color={muted} fontWeight="700" textTransform="uppercase">{l}</Text>
                        <Text fontSize="lg" fontWeight="900" color={c}>{v}</Text>
                    </Box>
                ))}
            </SimpleGrid>

            {error && <Alert status="warning" borderRadius="lg"><AlertIcon /><AlertDescription fontSize="sm">{error}</AlertDescription></Alert>}
            {!rows.length ? (
                <Text fontSize="sm" color={muted}>This road has no segments yet — draw it on the GIS map, then press “Refresh elevation” to slice it and fetch its ground.</Text>
            ) : withElev === 0 ? (
                <Alert status="info" borderRadius="lg"><AlertIcon /><AlertDescription fontSize="sm">No TessaDEM ground on these segments yet. Press “Refresh elevation”, or run the elevation backfill (road segments) for the whole network.</AlertDescription></Alert>
            ) : loading && !data ? <Skeleton h="170px" borderRadius="lg" /> : (
                <Box bg={card} borderWidth="1px" borderColor={border} borderRadius="lg" p={3}>
                    <ProfileChart profile={data?.profile} />
                </Box>
            )}

            {rows.length > 0 && (
                <Box overflowX="auto" maxH="420px" borderWidth="1px" borderColor={border} borderRadius="lg">
                    <Table size="sm">
                        <Thead bg={headBg} position="sticky" top={0} zIndex={1}>
                            <Tr><Th>Seg</Th><Th>Station</Th><Th>Ground start · mid · end</Th><Th>Grade</Th><Th>Water (forecast)</Th><Th>Starts</Th><Th>After the rain</Th><Th>Drain</Th></Tr>
                        </Thead>
                        <Tbody>
                            {rows.map((s) => {
                                const f = byId[String(s.id)];
                                const b = bandMeta(f?.band || 'none');
                                const g = [s.ground_start_elev_m, s.ground_mid_elev_m, s.ground_end_elev_m].map(num);
                                const slope = num(s.ground_slope_pct);
                                return (
                                    <Tr key={s.id}>
                                        <Td fontWeight="700">{s.segment_index}</Td>
                                        <Td fontSize="12px" whiteSpace="nowrap">{Math.round(num(s.station_start_m) || 0)}–{Math.round(num(s.station_end_m) || 0)} m</Td>
                                        <Td fontFamily="mono" fontSize="12px" whiteSpace="nowrap">{g.every((v) => v == null) ? '—' : g.map((v) => (v == null ? '—' : v.toFixed(2))).join(' · ')}</Td>
                                        <Td fontSize="12px" whiteSpace="nowrap">{slope == null ? '—' : `${slope > 0 ? '+' : ''}${slope.toFixed(2)} %`}</Td>
                                        <Td>
                                            {f && f.band !== 'none' ? (
                                                <Tooltip hasArrow label={(f.causes || []).join(' · ') || KIND_LABEL[f.kind] || ''}>
                                                    <HStack spacing={1}><Icon as={MdWaterDrop} color={b.hex} /><Text fontSize="13px" fontWeight="800" color={b.hex} whiteSpace="nowrap">{fmtWater(f)}</Text><Badge colorScheme={b.scheme} fontSize="10px">{b.label}</Badge></HStack>
                                                </Tooltip>
                                            ) : <Text fontSize="12px" color={muted}>{f ? 'clear' : (g.every((v) => v == null) ? 'no ground data' : 'clear')}</Text>}
                                        </Td>
                                        <Td fontSize="12px">{f && f.band !== 'none' ? fmtHours(f.time_to_ponding_hours) : '—'}</Td>
                                        <Td fontSize="12px">{f && f.band !== 'none' ? fmtClears(f) : '—'}</Td>
                                        <Td fontSize="12px">{f?.drains?.[0] ? `${f.drains[0].name || f.drains[0].asset_id} · ${f.drains[0].distance_m} m${f.drains[0].state && f.drains[0].state !== 'ok' ? ` · ${f.drains[0].state}` : ''}` : (f?.band && f.band !== 'none' ? 'none nearby' : '—')}</Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}
            {data?.assumptions?.length > 0 && <Text fontSize="11px" color={muted}>{data.assumptions.join(' ')}</Text>}
        </VStack>
    );
}

RoadTerrainPondingTab.propTypes = {
    roadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    roadName: PropTypes.string,
    segments: PropTypes.array,
    onChanged: PropTypes.func,
};

export default memo(RoadTerrainPondingTab);
